import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TinsmithLockup } from "@tinsmith/brand";

import styles from "../../auth/AuthScreen.module.css";
import { useSession } from "../../auth/session";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button, ButtonLink } from "../../components/Button";
import { Input } from "../../components/Input";
import { Pill } from "../../components/Pill";
import { ApiError } from "../../data/api/client";
import {
  isPasskeyCancellation,
  passkeysSupported,
} from "../../data/api/webauthn";
import {
  acceptInvitation,
  type InvitationPreview,
  previewInvitation,
} from "../../data/api/workspace";
import { paths } from "../../routing/paths";

// Public enrollment / invite-accept page, reached via a `/join?token=…` link.
// It's outside AuthGate because a brand-new user has no passkey to sign in with
// yet — here they mint one, which creates their account. An already-registered
// invitee signs in with their passkey and joins. See auth-and-sessions.md.

type Status = "error" | "loading" | "ready" | "working";

export const JoinPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { enroll, signInWithPasskey, user } = useSession();

  const [status, setStatus] = useState<Status>(token ? "loading" : "error");
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(
    token ? null : "This invitation link is missing its token.",
  );
  useDocumentTitle(
    preview?.workspace_name ? `Join ${preview.workspace_name}` : "Join",
  );

  useEffect(() => {
    if (!token) {
      return;
    }
    let live = true;
    previewInvitation(token)
      .then((p) => {
        if (!live) {
          return;
        }
        setPreview(p);
        setName(p.display_name ?? p.email.split("@")[0] ?? "");
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (!live) {
          return;
        }
        setError(
          err instanceof ApiError
            ? err.message
            : "This invitation link is not valid.",
        );
        setStatus("error");
      });
    return () => {
      live = false;
    };
  }, [token]);

  const supported = passkeysSupported();

  const fail = (err: unknown, fallback: string): void => {
    if (!isPasskeyCancellation(err)) {
      setError(err instanceof ApiError ? err.message : fallback);
    }
    setStatus("ready");
  };

  // New user: minting the passkey creates the account (+ joins the workspace).
  const createPasskey = (): void => {
    if (!token) {
      return;
    }
    setStatus("working");
    setError(null);
    enroll(token, name.trim() || (preview?.email ?? ""))
      .then(() => void navigate("/repos"))
      .catch((err: unknown) => fail(err, "Couldn't create your passkey."));
  };

  // Existing user: sign in with a passkey (if needed), then accept the invite.
  const signInAndJoin = (): void => {
    if (!token) {
      return;
    }
    setStatus("working");
    setError(null);
    void (async () => {
      if (!user) {
        await signInWithPasskey();
      }
      await acceptInvitation(token).catch(() => undefined);
      void navigate("/repos");
    })().catch((err: unknown) => fail(err, "Couldn't sign you in."));
  };

  const target = preview?.workspace_name ?? "Tinsmith";
  const notPending = preview && preview.status !== "pending";

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <TinsmithLockup size={24} />
        </div>

        {status === "loading" ? (
          <p className={styles.subtitle}>Loading your invitation…</p>
        ) : status === "error" || !preview ? (
          <>
            <h1 className={styles.title}>Invitation unavailable</h1>
            <p className={styles.subtitle}>{error}</p>
            <ButtonLink size="lg" to={paths.dashboard()} variant="accent">
              Go to Tinsmith
            </ButtonLink>
          </>
        ) : notPending || preview.expired ? (
          <>
            <h1 className={styles.title}>
              {preview.expired && preview.status === "pending"
                ? "This invitation expired"
                : `Invitation already ${preview.status}`}
            </h1>
            <p className={styles.subtitle}>Ask for a fresh invitation link.</p>
            <ButtonLink size="lg" to={paths.dashboard()} variant="accent">
              Go to Tinsmith
            </ButtonLink>
          </>
        ) : !supported ? (
          <>
            <h1 className={styles.title}>Passkeys not supported</h1>
            <p className={styles.subtitle}>
              This browser can't create a passkey. Open this link in a browser
              with Touch ID, Windows Hello, or a security key.
            </p>
          </>
        ) : preview.has_passkey ? (
          <>
            <h1 className={styles.title}>Join {target}</h1>
            <p className={styles.subtitle}>
              {preview.inviter_name ? (
                <strong>{preview.inviter_name}</strong>
              ) : (
                "You were"
              )}{" "}
              invited{" "}
              {preview.workspace_name ? `to ${preview.workspace_name}` : ""}
              {preview.role ? (
                <>
                  {" "}
                  as{" "}
                  <Pill status={preview.role === "admin" ? "done" : "review"}>
                    {preview.role}
                  </Pill>
                </>
              ) : null}
              . Sign in with your passkey to accept.
            </p>
            {error ? <div className={styles.error}>{error}</div> : null}
            <Button
              disabled={status === "working"}
              onClick={signInAndJoin}
              size="lg"
              variant="accent"
            >
              {status === "working" ? "Signing in…" : "Sign in with a passkey"}
            </Button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>
              {preview.workspace_name
                ? `Join ${preview.workspace_name}`
                : "Create your account"}
            </h1>
            <p className={styles.subtitle}>
              {preview.inviter_name ? (
                <>
                  <strong>{preview.inviter_name}</strong> invited{" "}
                  <strong>{preview.email}</strong>
                  {preview.role ? (
                    <>
                      {" "}
                      as{" "}
                      <Pill
                        status={preview.role === "admin" ? "done" : "review"}
                      >
                        {preview.role}
                      </Pill>
                    </>
                  ) : null}
                  . Create a passkey to finish.
                </>
              ) : (
                <>
                  Set up your passkey to create your account. No password
                  required.
                </>
              )}
            </p>
            <label className={styles.field}>
              <span className={styles.label}>Your name</span>
              <Input
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
                placeholder="Ada Lovelace"
                value={name}
              />
            </label>
            {error ? <div className={styles.error}>{error}</div> : null}
            <Button
              disabled={status === "working" || !name.trim()}
              onClick={createPasskey}
              size="lg"
              variant="accent"
            >
              {status === "working" ? "Creating…" : "Create your passkey"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
